/**
 * German (D-118): the learner's Goethe-Zertifikat levels, read once and shared by the screens of
 * the German track (`GET academy/goethe/`), and the format of the active level.
 */

import { getAcademyGoetheRetrieveQueryKey, useAcademyFormatRetrieve, useAcademyGoetheCreate, useAcademyGoetheRetrieve } from "@agencyos/api-client";
import { type GoetheLevel, type GoetheState, parseGoetheState, parseRatings, parseScale } from "@agencyos/core";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import { isObject } from "../types";

export const GOETHE = "goethe" as const;

export function useGoethe(enabled = true): { state: GoetheState | null; loading: boolean; refetch: () => Promise<unknown> } {
  const query = useAcademyGoetheRetrieve({ query: { retry: false, enabled } });
  const state = useMemo(() => (query.data?.status === 200 ? parseGoetheState(query.data.data) : null), [query.data]);
  return { state, loading: query.isLoading, refetch: query.refetch };
}

/** Prepare a level, change its date / centre / modules / target, choose a project. */
export function useGoetheSave() {
  const queryClient = useQueryClient();
  const mutation = useAcademyGoetheCreate({
    mutation: { onSuccess: () => void queryClient.invalidateQueries({ queryKey: getAcademyGoetheRetrieveQueryKey() }) },
  });
  return mutation;
}

/** The format of one level (sections, parts, rubrics) and the scale data of the table. */
export function useGoetheFormat(level: GoetheLevel | "") {
  const query = useAcademyFormatRetrieve({ exam: GOETHE, ...(level ? { level } : {}) }, { query: { retry: false, enabled: Boolean(level), staleTime: 600_000 } });
  return useMemo(() => {
    const data = query.data?.status === 200 ? (query.data.data as unknown) : null;
    const details = isObject(data) && isObject(data.details) ? data.details : {};
    const view = isObject(details.level) ? details.level : null;
    const common = isObject(details.common) ? details.common : {};
    return {
      view,
      scale: parseScale(details),
      ratings: parseRatings(common.ratings),
      loading: query.isLoading,
    };
  }, [query.data, query.isLoading]);
}
